import { useState, useEffect, useRef, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { useAppStore } from '../../store/appStore'

// ── Types ────────────────────────────────────────────────────
export interface AgentPhase {
  key: string
  id: string
  label: string
  lines: Array<{ text: string; type?: string }>
}

interface AgentStreamRendererProps {
  goal: string
  frozenPhases?: AgentPhase[]
  frozenDone?: boolean
  onComplete?: (content: string, filesModified?: string[], phases?: AgentPhase[]) => void
  onError?: (msg: string) => void
}

interface StreamLine {
  id: number
  text: string
  type: 'status' | 'cmd' | 'output' | 'success' | 'error' | 'diff-add' | 'diff-rm' | 'diff-header' | 'file' | 'approval'
}

// ── Main Component ───────────────────────────────────────────
export function AgentStreamRenderer({ goal, frozenPhases, frozenDone, onComplete, onError }: AgentStreamRendererProps) {
  const isFrozen = !!frozenPhases
  const [lines, setLines] = useState<StreamLine[]>([])
  const [runId, setRunId] = useState('')
  const [done, setDone] = useState(frozenDone ?? false)
  const [waitingApproval, setWaitingApproval] = useState(false)
  const [approvalItems, setApprovalItems] = useState<string[]>([])
  const [filesModified, setFilesModified] = useState<string[]>([])
  const filesModifiedRef = useRef<string[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const lineIdRef = useRef(0)
  const startedRef = useRef(false)
  // collect full text output for history
  const fullTextRef = useRef('')

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight
    })
  }, [])

  const addLine = useCallback((text: string, type: StreamLine['type']) => {
    const id = ++lineIdRef.current
    setLines(prev => [...prev, { id, text, type }])
    scrollToBottom()
  }, [scrollToBottom])

  const handleApprove = useCallback(async () => {
    if (!runId) return
    setWaitingApproval(false)
    setApprovalItems([])
    addLine('✓ Approved', 'success')
    await authFetch(apiUrl('/api/agent-stream'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approveRunId: runId }),
    }).catch(() => {})
  }, [runId, addLine])

  const handleReject = useCallback(() => {
    setWaitingApproval(false)
    setApprovalItems([])
    addLine('✗ Rejected', 'error')
  }, [addLine])

  // ── Frozen mode (chat history) ─────────────────────────────
  if (isFrozen) {
    const totalFiles = frozenPhases?.reduce((acc, p) => acc + p.lines.filter(l => l.type === 'file').length, 0) || 0
    return (
      <div style={styles.frozenCard}>
        <div style={styles.frozenHeader}>
          <span style={styles.frozenDot} />
          <span style={styles.frozenLabel}>Agent completed</span>
        </div>
        <div style={styles.frozenGoal}>{goal}</div>
        {totalFiles > 0 && <div style={styles.frozenMeta}>{totalFiles} files modified</div>}
      </div>
    )
  }

  // ── Stream mode ────────────────────────────────────────────
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const run = async () => {
      try {
        const res = await authFetch(apiUrl('/api/agent-stream'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ goal, projectName: (window as any).__pulseActiveProject || '', agentMode: localStorage.getItem('pulse-agent-mode') || 'assisted' }),
        })
        if (!res.ok || !res.body) { onError?.('Agent request failed'); return }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''

        while (true) {
          const { done: streamDone, value } = await reader.read()
          if (streamDone) break
          buf += decoder.decode(value, { stream: true })
          const rawLines = buf.split('\n')
          buf = rawLines.pop() ?? ''

          for (const raw of rawLines) {
            const t = raw.trim()
            if (!t.startsWith('data: ')) continue
            try {
              const evt = JSON.parse(t.slice(6))
              handleEvent(evt)
            } catch {}
          }
        }
      } catch (err: unknown) {
        onError?.(err instanceof Error ? err.message : 'Agent failed')
      }
    }

    const handleEvent = (evt: any) => {
      switch (evt.type) {
        case 'ping':
          setRunId(evt.runId || '')
          break

        case 'phase':
          // Only show investigation and applying phases — hide the rest
          if (evt.phase === 'analyzing' && evt.label?.includes('Investigating')) {
            addLine('Investigating...', 'status')
          } else if (evt.phase === 'applying') {
            addLine('Applying changes...', 'status')
          }
          phasesRef.current.push({ key: evt.phase, id: evt.phase, label: evt.label || '', lines: [] })
          break

        case 'phase_complete':
          break

        case 'command_start':
          addLine(`$ ${evt.command}`, 'cmd')
          break

        case 'command_output': {
          const out = evt.output || ''
          // Hide all noise — only show tool execution results
          if (out.startsWith('✓ Skill:') || out.startsWith('✓ Read ') || out.startsWith('✓ Write ') || out.startsWith('> scan') || out.includes(' lines)') || out.startsWith('✓ server/') || out.startsWith('✓ src/') || out.startsWith('✓ Stack:') || out.startsWith('✓ Goal') || out.startsWith('✓ Project:') || out.startsWith('✓ Framework:') || out.startsWith('1. ') || out.startsWith('2. ') || out.startsWith('3. ')) break
          if (out.startsWith('📄 ')) break
          if (out.startsWith('✓ ') && out.includes('.json')) break
          if (out.startsWith('✓ ') && out.includes('.tsx')) break
          if (out.startsWith('✓ ') && out.includes('.ts')) break
          if (out.startsWith('✓ ') && out.includes('.env')) break
          if (out.startsWith('✓ ') && out.includes('.cjs')) break
          if (out.startsWith('✓ ') && out.includes('.js')) break
          if (out.startsWith('✓') && !out.includes('patched') && !out.includes('Done') && !out.includes('Approved') && out.length < 60 && out.includes('/')) break
          if (out.startsWith('✓')) addLine(out, 'success')
          else if (out.startsWith('✗') || out.startsWith('⚠')) addLine(out, 'error')
          else if (out.startsWith('>') || out.startsWith('▶')) addLine(out, 'cmd')
          else addLine(out, 'output')
          break
        }

        case 'diff': {
          const dl = (evt.diff || '').trim()
          if (!dl) break
          if (dl.startsWith('---') || dl.startsWith('@@')) addLine(dl, 'diff-header')
          else if (dl.startsWith('+')) addLine(dl, 'diff-add')
          else if (dl.startsWith('-')) addLine(dl, 'diff-rm')
          else addLine(dl, 'output')
          break
        }

        case 'file_action':
          // Only show writes/creates, not reads
          if (evt.action === 'write' || evt.action === 'create') {
            addLine(`✓ ${evt.path}`, 'success')
            filesModifiedRef.current = [...filesModifiedRef.current.filter(p => p !== evt.path), evt.path]
            setFilesModified([...filesModifiedRef.current])
          }
          break

        case 'file_patched':
          if (evt.path) {
            const sp = `/${evt.path.replace(/\\/g, '/')}`
            useAppStore.getState().setAgentFilePatch(sp, evt.oldContent ?? '', evt.newContent ?? '')
            window.dispatchEvent(new CustomEvent('pulse:agent-file-patched', { detail: { path: sp, content: evt.newContent } }))
          }
          break

        case 'approval_required':
          setApprovalItems(evt.files?.length ? evt.files : evt.patches?.map((p: any) => p.filePath) ?? [])
          setWaitingApproval(true)
          break

        case 'text':
          if (evt.text?.match(/^(Stack:|Port:|Routes:)/)) break
          if (evt.text?.trim()) {
            fullTextRef.current += evt.text.trim() + '\n'
            addLine(evt.text.trim(), 'output')
          }
          break

        case 'retry':
          addLine(`↻ Retry ${evt.attempt}: ${(evt.reason || '').slice(0, 60)}`, 'error')
          break

        case 'error':
          addLine(`❌ ${evt.message}`, 'error')
          break

        case 'restart_backend':
          addLine('🔄 Restarting backend...', 'status')
          break

        case 'summary':
          if (evt.filesModified?.length) {
            filesModifiedRef.current = evt.filesModified
            setFilesModified(evt.filesModified)
            addLine(`✓ Done — ${evt.filesModified.length} file(s) modified`, 'success')
          }
          break

        case 'done':
          setDone(true)
          onComplete?.(fullTextRef.current || '', filesModifiedRef.current, phasesRef.current)
          break
      }
    }

    run()
  }, [goal, addLine, onComplete, onError, scrollToBottom])

  return (
    <div style={styles.root}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ ...styles.dot, background: done ? '#5fd4a4' : '#7c6ef5' }} />
        <span style={styles.title}>pulse agent</span>
        <span style={{ ...styles.badge, ...(done ? styles.badgeDone : {}) }}>
          {done ? '✓ done' : '● running'}
        </span>
      </div>

      {/* Goal */}
      <div style={styles.goal}>{goal}</div>

      {/* Stream output */}
      <div ref={containerRef} style={styles.body}>
        {lines.map(line => (
          <div key={line.id} style={{ ...styles.line, ...getLineStyle(line.type) }}>
            {line.text}
          </div>
        ))}

        {/* Approval card */}
        {waitingApproval && (
          <div style={styles.approvalCard}>
            <div style={styles.approvalTitle}>Allow changes?</div>
            {approvalItems.map(f => (
              <div key={f} style={styles.approvalFile}>→ {f}</div>
            ))}
            <div style={styles.approvalBtns}>
              <button onClick={handleApprove} style={styles.btnApprove}>✓ Approve</button>
              <button onClick={handleReject} style={styles.btnReject}>✗ Reject</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────
function getPhaseIcon(phase: string): string {
  const icons: Record<string, string> = {
    understanding: '🧠', memory: '💾', skills: '⚡', permissions: '🔐',
    discovering: '🔭', reading: '📖', analyzing: '🧬', generating: '✨',
    applying: '🚀', verifying: '🧪', done: '🎉',
  }
  return icons[phase] || '●'
}

function getLineStyle(type: StreamLine['type']): React.CSSProperties {
  switch (type) {
    case 'cmd': return { color: '#a78bfa', background: 'rgba(0,0,0,0.4)', borderRadius: 6, padding: '6px 12px', borderLeft: '3px solid #7c6ef5', margin: '4px 0' }
    case 'output': return { color: 'rgba(255,255,255,0.5)', paddingLeft: 12 }
    case 'success': return { color: '#5fd4a4' }
    case 'error': return { color: '#f07070' }
    case 'status': return { color: 'rgba(255,255,255,0.7)', fontWeight: 600, margin: '8px 0 2px' }
    case 'file': return { color: '#38bdf8', fontSize: 11, opacity: 0.7 }
    case 'diff-header': return { color: '#a78bfa', background: 'rgba(124,110,245,0.06)', borderRadius: 4, padding: '2px 8px', fontSize: 10 }
    case 'diff-add': return { color: '#5fd4a4', background: 'rgba(95,212,164,0.08)', borderRadius: 4, padding: '2px 8px', borderLeft: '2px solid rgba(95,212,164,0.5)' }
    case 'diff-rm': return { color: '#f07070', background: 'rgba(240,112,112,0.08)', borderRadius: 4, padding: '2px 8px', borderLeft: '2px solid rgba(240,112,112,0.4)', textDecoration: 'line-through', opacity: 0.7 }
    case 'approval': return { color: '#f5a157' }
    default: return {}
  }
}

// ── Styles ───────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  root: { background: 'rgba(6,6,18,0.97)', borderRadius: 14, border: '1px solid rgba(124,110,245,0.12)', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  dot: { width: 8, height: 8, borderRadius: '50%', boxShadow: '0 0 8px rgba(124,110,245,0.5)' },
  title: { font: '600 10px/1 ui-monospace, monospace', color: 'rgba(255,255,255,0.35)', letterSpacing: 0.5, textTransform: 'uppercase' as const },
  badge: { marginLeft: 'auto', padding: '3px 8px', borderRadius: 12, font: '700 9px/1 system-ui', background: 'rgba(124,110,245,0.1)', border: '1px solid rgba(124,110,245,0.25)', color: '#a78bfa' },
  badgeDone: { background: 'rgba(95,212,164,0.1)', borderColor: 'rgba(95,212,164,0.25)', color: '#5fd4a4' },
  goal: { padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.03)', font: '11px/1.4 ui-monospace, monospace', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  body: { flex: 1, overflowY: 'auto' as const, padding: '12px 16px', display: 'flex', flexDirection: 'column' as const, gap: 3, scrollbarWidth: 'thin' as const },
  line: { font: '11px/1.5 ui-monospace, "SF Mono", monospace', whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const, animation: 'fadeSlideIn 0.2s ease-out' },
  approvalCard: { margin: '12px 0', padding: 16, borderRadius: 12, border: '1px solid rgba(245,161,87,0.3)', background: 'rgba(245,161,87,0.04)' },
  approvalTitle: { font: '700 13px/1 system-ui', color: '#f5a157', marginBottom: 10 },
  approvalFile: { font: '11px/1.5 ui-monospace, monospace', color: 'rgba(255,255,255,0.6)', padding: '3px 10px', background: 'rgba(0,0,0,0.2)', borderRadius: 5, borderLeft: '2px solid #f5a157', marginBottom: 4 },
  approvalBtns: { display: 'flex', gap: 10, marginTop: 12 },
  btnApprove: { padding: '8px 18px', borderRadius: 10, font: '700 12px/1 system-ui', background: 'rgba(95,212,164,0.12)', border: '1px solid rgba(95,212,164,0.35)', color: '#5fd4a4', cursor: 'pointer' },
  btnReject: { padding: '8px 14px', borderRadius: 10, font: '700 12px/1 system-ui', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' },
  frozenCard: { padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(95,212,164,0.15)', background: 'rgba(95,212,164,0.03)' },
  frozenHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  frozenDot: { width: 6, height: 6, borderRadius: '50%', background: '#5fd4a4' },
  frozenLabel: { font: '700 11px/1 system-ui', color: '#5fd4a4' },
  frozenGoal: { font: '11px/1.4 ui-monospace, monospace', color: 'rgba(255,255,255,0.5)' },
  frozenMeta: { font: '10px/1 system-ui', color: 'rgba(255,255,255,0.3)', marginTop: 6 },
}
